import { Box, Container, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Award, Layers, Pencil, Wrench, MessageSquare, ClipboardList, ShieldCheck, MapPin, Users, CheckCircle } from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const reasons = [
  {
    icon: Award,
    number: '01',
    title: 'Proven Experience You Can Trust',
    content: `With over 500 homes built across New South Wales, Clarence Homes brings proven experience across all types of residential construction — from standard builds to complex, multi‑storey dwelling houses. Our track record reflects consistency, quality, and reliable project delivery.`,
  },
  {
    icon: Layers,
    number: '02',
    title: 'All‑In‑One Residential Builder',
    content: null,
    intro: `We offer a complete range of residential building services under one roof, including:`,
    bullets: [
      'Land & build packages',
      'Build‑only services',
      'Renovations and extensions',
      'Granny flats',
      'Custom single‑storey and double‑storey homes',
      'Complex and multi‑storey residential builds',
    ],
    outro: `This means fewer handovers, clearer communication, and better control over quality.`,
  },
  {
    icon: Pencil,
    number: '03',
    title: 'Custom‑Built, Not Mass‑Produced',
    content: `Every Clarence Homes project is custom designed and custom built. We don't use cookie‑cutter designs — your home is tailored to your lifestyle, block, and budget, ensuring practical layouts and long‑term value.`,
  },
  {
    icon: Wrench,
    number: '04',
    title: 'Expertise in Complex Builds',
    content: `We specialise in technically challenging and complex residential projects, including multi‑storey homes and detailed architectural designs. Our experience allows us to confidently manage complex structures, approvals, and construction requirements.`,
  },
  {
    icon: MessageSquare,
    number: '05',
    title: 'Transparent & Honest Process',
    content: `We believe in clear communication, honest advice, and transparency throughout the build. From pricing to timelines, we keep you informed at every stage so there are no surprises.`,
  },
  {
    icon: ClipboardList,
    number: '06',
    title: 'End‑to‑End Project Management',
    content: `From initial consultation and design through approvals, construction, and final handover, we manage the entire process. Our structured approach ensures efficiency, quality control, and peace of mind.`,
  },
  {
    icon: ShieldCheck,
    number: '07',
    title: 'Quality Workmanship & Materials',
    content: `We are committed to high standards of construction, using quality materials and skilled trades to deliver homes that are built to last and comply with Australian standards.`,
  },
  {
    icon: MapPin,
    number: '08',
    title: 'Local NSW Knowledge',
    content: `Building across NSW, we understand local council requirements, CDC and DA approval processes, and site conditions — helping projects move forward smoothly and efficiently.`,
  },
  {
    icon: Users,
    number: '09',
    title: 'Client‑Focused Approach',
    content: `At Clarence Homes, our clients come first. We listen, plan carefully, and work closely with you to ensure your vision is delivered exactly as intended.`,
  },
];

const buildWith = [
  '500+ homes built across NSW',
  'A builder experienced in all residential building types',
  'Custom designs and complex builds',
  'Clear communication and honest advice',
  'Quality you can trust',
];

function WhyChooseUs() {
  useEffect(() => {
    AOS.init({ duration: 800, once: false, offset: 100, easing: 'ease-out-cubic' });
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f8f8', overflow: 'hidden' }}>

      {/* ── HERO ── */}
      <Box sx={{
        backgroundColor: 'var(--primary-color)',
        py: { xs: 7, md: 11 },
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute',
          top: '-80px', right: '-80px',
          width: '320px', height: '320px',
          borderRadius: '50%',
          border: '70px solid rgba(192,174,93,0.07)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""', position: 'absolute',
          bottom: '-90px', left: '-50px',
          width: '280px', height: '280px',
          borderRadius: '50%',
          border: '55px solid rgba(192,174,93,0.05)',
          pointerEvents: 'none',
        },
      }}>
        <Typography sx={{ fontSize: { xs: '32px', md: '52px' }, fontWeight: 800, color: '#fff', letterSpacing: 2, lineHeight: 1.1 }}>
          WHY CHOOSE US
        </Typography>
        <Box sx={{ width: '80px', height: '4px', backgroundColor: 'var(--primary-text-color)', mx: 'auto', mt: 3, mb: 2.5 }} />
        <Typography sx={{ fontSize: { xs: '15px', md: '17px' }, color: 'rgba(255,255,255,0.65)', maxWidth: 560, mx: 'auto', lineHeight: 1.85, px: 2 }}>
          Why Choose Clarence Homes
        </Typography>
      </Box>

      {/* ── GOLD BAR ── */}
      <Box sx={{ backgroundColor: 'var(--primary-text-color)', py: 2 }}>
        <Container maxWidth="xl">
          <Typography sx={{ fontSize: '13px', color: 'var(--primary-color)', fontWeight: 700, textAlign: 'center' }}>
            Clarence Homes – Residential building done right.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>

        {/* ── REASON CARDS ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 7 }}>
          {reasons.map((item, index) => {
            const Icon = item.icon;
            return (
              <Box
                key={index}
                data-aos="fade-up"
                data-aos-delay={(index % 4) * 60}
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 20px rgba(19,25,83,0.07)',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 36px rgba(19,25,83,0.13)',
                    transform: 'translateY(-6px)',
                  }
                }}
              >
                {/* Navy strip */}
                <Box sx={{
                  width: { xs: '100%', sm: '90px' },
                  backgroundColor: 'var(--primary-color)',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  py: { xs: 2.5, sm: 0 }, gap: 1.5, flexShrink: 0,
                }}>
                  <Icon size={26} color="var(--primary-text-color)" />
                  <Typography sx={{ fontSize: '20px', fontWeight: 800, color: 'rgba(255,255,255,0.18)', lineHeight: 1 }}>
                    {item.number}
                  </Typography>
                </Box>

                {/* Content */}
                <Box sx={{ p: { xs: 3, md: 4.5 }, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: '4px', height: '22px', backgroundColor: 'var(--primary-text-color)', borderRadius: '2px', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: 700, color: 'var(--primary-color)' }}>
                      {item.title}
                    </Typography>
                  </Box>

                  {item.content && (
                    <Typography sx={{ fontSize: '15px', color: '#555', lineHeight: 1.95 }}>
                      {item.content}
                    </Typography>
                  )}

                  {item.intro && (
                    <>
                      <Typography sx={{ fontSize: '15px', color: '#555', lineHeight: 1.95, mb: 2 }}>
                        {item.intro}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mb: item.outro ? 2 : 0 }}>
                        {item.bullets.map((b, bi) => (
                          <Box key={bi} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                              width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                              backgroundColor: 'var(--primary-color)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <CheckCircle size={12} color="var(--primary-text-color)" />
                            </Box>
                            <Typography sx={{ fontSize: '15px', color: '#444', lineHeight: 1.7 }}>{b}</Typography>
                          </Box>
                        ))}
                      </Box>
                      {item.outro && (
                        <Typography sx={{ fontSize: '15px', color: '#555', lineHeight: 1.95, fontStyle: 'italic' }}>
                          {item.outro}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ── BUILD WITH CONFIDENCE ── */}
        <Box
          data-aos="fade-up"
          sx={{
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(19,25,83,0.12)',
          }}
        >
          {/* Header */}
          <Box sx={{
            backgroundColor: 'var(--primary-color)',
            px: { xs: 3, md: 5 }, py: 3,
            display: 'flex', alignItems: 'center', gap: 2,
          }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: '10px',
              backgroundColor: 'rgba(192,174,93,0.15)',
              border: '1.5px solid var(--primary-text-color)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <ShieldCheck size={22} color="var(--primary-text-color)" />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '11px', color: 'var(--primary-text-color)', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>
                Your Decision
              </Typography>
              <Typography sx={{ fontSize: { xs: '18px', md: '22px' }, fontWeight: 800, color: '#fff' }}>
                Build With Confidence
              </Typography>
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ backgroundColor: '#fff', px: { xs: 3, md: 5 }, py: 4 }}>
            <Typography sx={{ fontSize: '15px', color: '#555', lineHeight: 1.9, mb: 3 }}>
              When you choose Clarence Homes, you're choosing:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {buildWith.map((item, i) => (
                <Box
                  key={i}
                  data-aos="zoom-in"
                  data-aos-delay={i * 60}
                  sx={{
                    flex: '1 1 220px',
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    p: 2.5,
                    borderRadius: '8px',
                    backgroundColor: '#f8f8f8',
                    border: '1.5px solid rgba(19,25,83,0.08)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'var(--primary-text-color)',
                      backgroundColor: '#fff',
                      transform: 'translateY(-6px)',
                      boxShadow: '0 10px 36px rgba(19,25,83,0.13)',
                    }
                  }}
                >
                  <Box sx={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                    backgroundColor: 'var(--primary-color)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CheckCircle size={14} color="var(--primary-text-color)" />
                  </Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'var(--primary-color)' }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

      </Container>
    </Box>
  );
}

export default WhyChooseUs;
